import {
  MFA_CONFIG_ENTITLEMENT_SERVICE_API,
  APP_DETAILS_SERVICE_API,
  AUTH_ACCESS_TOKEN_API,
  USER_PROFILE_SERVICE_API,
  POLL_UNREAD_NOTIFS_COUNT_ENDPOINT,
  UPDATE_MSG_ARCHIVE_ENDPOINT,
  GET_MESSAGES_ENDPOINT,
  MARK_MSGS_READ_ENDPOINT,
  CREATE_USER_IN_AMP_ENDPOINT,
  UPDATE_USER_ONBOARD_STATUS_API,
  GET_USER_ONBOARD_STATUS_ENDPOINT,
  REVOKE_ACCESS_TOKEN_API,
  EXPIRE_GUID_API,
  VALIDATE_USER_IMPERSONATION_API,
  GUEST_ACCESS_SERVICE_URL,
  GUEST_USER_PROFILE_API,
  THIRD_PARTY_ENTITLEMENT_SERVICE_API,
} from '../constants/AppConstants';
import { GUEST_ERROR_MESSAGES } from '../constants/GuestAccess';

/**
 * Service to fetch entitled applications
 * @returns {Promise}
 */
export const getMFAConfigService = () => fetch(`${MFA_CONFIG_ENTITLEMENT_SERVICE_API}`).then((response) => (response.json())).catch(() => {});

// #genai Service to fetch app details by appCode (shared-resource flow).
// Auth header injected automatically by the fetch interceptor.
export const getAppDetailsService = (appCode) => fetch(`${APP_DETAILS_SERVICE_API}/${appCode}`).then((response) => (response.json())).catch(() => {});

/**
 * Service to exchange auth token for auth code
 * @param {String} code - Okta auth code
 * @param {String} redirectUrl - redirectUrl used to Okta login
 * @returns {Promise}
 */
export const getAccessTokenService = (code, redirectUrl) => fetch(AUTH_ACCESS_TOKEN_API, {
  method: 'POST',
  headers: new Headers({
    'content-type': 'application/x-www-form-urlencoded',
    Accept: 'application/json',
  }),
  credentials: 'include',
  body: `grant_type=authorization_code&code=${code}&redirect_uri=${redirectUrl}`,
})
  .then((response) => (response.json()));

/**
 * Service to get new auth token using refresh token.
 * For SHARE tokens, pass shareGuid (tokenGUID from the JWT) so the backend
 * can locate the correct refresh-token record. #genai
 */
export const getNewAccessTokenService = (redirectUrl, impersonationGuid = '', shareGuid = '') => fetch(AUTH_ACCESS_TOKEN_API, {
  method: 'POST',
  headers: new Headers({
    'content-type': 'application/x-www-form-urlencoded',
    Accept: 'application/json',
    ...(impersonationGuid && { signInAsGuid: impersonationGuid }),
    ...(shareGuid && { shareGuid }),
  }),
  credentials: 'include',
  body: `grant_type=refresh_token&scope=offline_access openid&redirect_uri=${redirectUrl}`,
}).then(async (response) => {
  try {
    const json = await response.json();
    return { ok: response.ok, json, status: response.status };
  } catch (error) {
    return { ok: false, json: { errorDescription: error.message } };
  }
}).catch((error) => ({ ok: false, json: { errorDescription: error.message } }));

/**
 * Service to fetch logged in user's details
 * @returns {Object} - response data received
 */
export const getUserDetailsService = (fetchUserApp) => fetch(`${USER_PROFILE_SERVICE_API}?fetchUserApp=${fetchUserApp}`)
  .then((response) => {
    if (!response.ok) {
      const error = new Error(response.json.errorDescription);
      error.errorCode = response.json.errorCode;
      throw error;
    }
    return response;
  })
  .then((response) => (response.json())).catch((error) => { throw error; });

// #genai v2 user profile for guest users — bearer token is injected by fetch interceptor.
export const getGuestUserProfileService = () => fetch(GUEST_USER_PROFILE_API)
  .then((response) => {
    if (!response.ok) {
      const error = new Error(GUEST_ERROR_MESSAGES.GUEST_PROFILE_FETCH_FAILED);
      throw error;
    }
    return response.json();
  });

/**
 * Service to update the user details
 * @param {Object} requestBody - set of details to be passed in body to be updated
 * @returns {Object} - Json
 */
export const putUpdateProfileService = (requestBody) => fetch(USER_PROFILE_SERVICE_API, {
  method: 'PUT',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(requestBody),
})
  .then(async (response) => {
    try {
      const json = await response.json();
      return { ok: response.ok, json };
    } catch (error) {
      return { ok: false, json: { errorCode: '40029', errorDescription: 'Internal server error' } };
    }
  });

/**
* Service to create user in Account management platform
* @param {Object} userName
* @returns {Object} - Json
*/
export const createUserInAMP = (userName) => fetch(`${CREATE_USER_IN_AMP_ENDPOINT + userName}`, {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
})
  .then(async (response) => {
    try {
      if (response.status === 201) {
        return { ok: response.ok };
      }
      const json = await response.json();
      return { ok: response.ok, json };
    } catch (error) {
      return { ok: false, json: { errorDescription: error.message } };
    }
  });

/**
 * Service to get unread notifications count
 * @param {Object} userId
 * @returns {Object} - Json
 */
export const getUnreadMsgsCount = () => fetch(POLL_UNREAD_NOTIFS_COUNT_ENDPOINT, {
  method: 'GET',
})
  .then(async (response) => {
    try {
      if (response.status === 202) {
        return { status: response.status };
      }
      const json = await response.json();
      return { status: response.status, json };
    } catch (error) {
      return { ok: false, json: { errorCode: '40030', errorDescription: 'Internal server error' } };
    }
  });

/**
 * Service to mark a notification as archived
 * @param {Object} notificationId
 * @returns {Object} - Json
 */
export const markArchive = (notificationId) => fetch(`${UPDATE_MSG_ARCHIVE_ENDPOINT}${notificationId}`, {
  method: 'PATCH',
  body: JSON.stringify({ archived: true }),
})
  .then(async (response) => {
    try {
      if (response.status === 200) {
        const json = await response.json();
        return { ok: true, json };
      }
      return { ok: false };
    } catch (error) {
      return { ok: false, json: { errorCode: '40031', errorDescription: `Internal server error - ${error}` } };
    }
  });

/**
 * Service to get notification messages of last 7 days
 * @param {Object}
 * @returns {Object} - Json
 */
export async function getMessagesAPICall() {
  try {
    const url = GET_MESSAGES_ENDPOINT;
    const response = await fetch(url, {
      method: 'GET',
    });
    if (response.status === 204) {
      return { status: response.status };
    }
    const json = await response.json();
    return { status: response.status, json };
  } catch (error) {
    return 'error';
  }
}

/**
 * Service to mark messages as read
 * @param {Object} userId and notificationIDs
 * @returns {Object} - Json
 */
export async function putReadMessages(reqBody) {
  const url = MARK_MSGS_READ_ENDPOINT;
  return fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: reqBody,
  }).then(async (response) => {
    try {
      if (response.status === 200) {
        return { ok: true, status: response.status };
      }
      const json = await response.json();
      return { ok: false, json, status: response.status };
    } catch (error) {
      return { ok: false, json: { errorDescription: error.message } };
    }
  }).catch((error) => ({ ok: false, json: { errorDescription: error.message } }));
}

/**
 * Service to get user entitled apps
 */
export const fetchUserEntitledAppsService = () => fetch(`${THIRD_PARTY_ENTITLEMENT_SERVICE_API}`, {
  method: 'GET',
}).then(async (response) => {
  try {
    const json = await response.json();
    return { ok: response.ok, json, status: response.status };
  } catch (error) {
    return { ok: false, json: { errorDescription: error.message } };
  }
}).catch((error) => ({ ok: false, json: { errorDescription: error.message } }));

/**
 * Service to update user onboard status
 * @returns {Object} - Json
 */
export const updateUserOnboardStatusService = () => fetch(UPDATE_USER_ONBOARD_STATUS_API, {
  method: 'PATCH',
}).then(async (response) => {
  try {
    if (response.ok) {
      return { ok: response.ok };
    }
    const json = await response.json();
    return { ok: response.ok, json };
  } catch (error) {
    return { ok: false, json: { errorDescription: error.message } };
  }
}).catch((error) => ({ ok: false, json: { errorDescription: error.message } }));

/**
 * Service to get user entitled apps
 */
export const getUserOnboardingStatus = () => fetch(GET_USER_ONBOARD_STATUS_ENDPOINT, {
  method: 'GET',
}).then(async (response) => {
  try {
    const json = await response.json();
    return { ok: response.ok, json, status: response.status };
  } catch (error) {
    return { ok: false, json: { errorDescription: error.message } };
  }
}).catch((error) => ({ ok: false, json: { errorDescription: error.message } }));

/**
 * Service to revoke access token
 */
export const revokeAccessToken = () => fetch(REVOKE_ACCESS_TOKEN_API, {
  method: 'POST',
}).then(async (response) => {
  try {
    const json = await response.json();
    return { ok: response.ok, json, status: response.status };
  } catch (error) {
    return { ok: false, json: { errorDescription: error.message } };
  }
}).catch((error) => ({ ok: false, json: { errorDescription: error.message } }));

/**
 * Service to expire guid
 */
export const expireGuid = (guid) => fetch(EXPIRE_GUID_API.replace('$guid$', guid), {
  method: 'PUT',
}).then(async (response) => {
  if (response.status === 204) {
    // Successful request with no content
    return { ok: true, json: null, status: 204 };
  }

  // Attempt to parse JSON error response
  try {
    const json = await response.json();
    return { ok: response.ok, json, status: response.status };
  } catch (error) {
    return { ok: false, json: { errorDescription: error.message } };
  }
})
  .catch((error) => ({ ok: false, json: { errorDescription: error.message } }));

/**
 * Service to validate the sign in as guid
 */
export async function validateUserImpersonationService(impersonationGuid) {
  try {
    const response = await fetch(`${VALIDATE_USER_IMPERSONATION_API}/${impersonationGuid}`, {
      method: 'GET',
      skip401Handling: true,
    });
    let json = {};
    if (response.status !== 204) {
      json = await response.json();
    }
    return { ok: response.ok, json };
  } catch (error) {
    return { ok: false, json: error };
  }
}
/**
 * Service to update user access audit for shared resource login
 * Updates the login status and session information in the user_access_audit table.
 * - Guest users: Updates login_status and session_id
 * - EV users: Updates only login_status
 * User type is automatically determined from the JWT token.
 * @param {string} loginId - UUID format (e.g., "550e8400-e29b-41d4-a716-446655440000")
 * @param {object} requestBody - { status: 'PENDING'|'SUCCESS'|'FAILED', failureReason?: string }
 * @returns {Promise<{ ok, json, status }>}
 */
// #genai
export const updateUserAccessAuditService = (loginId, requestBody) => {
  // Auth header is injected by the fetch interceptor (Bearer token from getAuthToken()).
  const url = `${GUEST_ACCESS_SERVICE_URL}/share/resource-user/login/${loginId}/status`;
  return fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  })
    .then(async (response) => {
      if (response.status === 204) {
        return { ok: response.ok, json: null, status: response.status };
      }
      try {
        const text = await response.text();
        const json = text ? JSON.parse(text) : null;
        return { ok: response.ok, json, status: response.status };
      } catch (error) {
        return { ok: false, json: { errorDescription: error.message } };
      }
    })
    .catch((error) => ({ ok: false, json: { errorDescription: error.message } }));
};

// validateResourcePathService and requestPreAccessTokenService have been moved
// to services/GuestAccess.service.js as part of the guest-access feature.
